import { routesList } from "../constants";
import Observer from "../observer";
import { localStorageAPI } from "../localStorageAPI";
import { sentryInstance } from "../sentry";


import geojsonhint from '@mapbox/geojsonhint';
import togeojson from '@mapbox/togeojson';

const geoJsonFeaturesTypes = {
    line: 'LineString',
    multiLine: "MultiLineString"
};
const defaultRouteSettings = {
    prevRouteId: new Date().getTime().toString(),
    routeId: new Date().getTime().toString(),
    geoJson: null,
    bounds: [],
    routeColor: '#ec3535',
    routeWidth: 14,
};
const gpxExtension = 'gpx';
const supportedRouteFileExtension = ['json', 'js', 'geojson', gpxExtension]

export class RouteModel extends Observer{

    constructor() {
        super();
        const route = yourMap.route || localStorageAPI.getJson();
        this._routeSettings = {
            ...defaultRouteSettings,
            ...route,
            ...{
                enableFitToTheBounds: false,
                title: '',
                subtitle: ''
            }
        };
    }

    init() {
        const { geoJson } = this.getRouteSettings();
        if(geoJson) {
            this.publish('route:data:ready');
        }
    }

    getRouteSettings() {
        return this._routeSettings
    }

    setRouteColor(color){
        localStorageAPI.update({
            routeColor: color
        });
        this._routeSettings.routeColor = color;
    }

    setRouteWidth(width) {
        localStorageAPI.update({
            routeWidth: width
        });
        this._routeSettings.routeWidth = width;
    }

    onSelectRoute(routeId){
        const routeData = routesList.routes.find(route => route.id === routeId);
        this._requestGeoJsonData(routeData.fileName)
            .then(geoJson => this._parseSuccessResponse(geoJson, routeData))
            .catch((error) => {
                this.publish('errorEmit');
                sentryInstance.sendInfoReport('catch error on load selected route')
            });
    }
    addCustomRouteFile(file){
        this._readRouteFile(file)
            .then(({ result, fileExtension }) => {
                this.addCustomRoute(result, fileExtension);
            })
            .catch((error)=>{
                this.publish('errorEmit');
                sentryInstance.sendInfoReport('catch error on add custom route')
            });
    }
    addCustomRoute(rawData, fileExtension){
        this._logCustomRouteData(rawData);
        const geojson = this._parseToGeojson(rawData, fileExtension);

        if(geojson) {
            this._parseSuccessResponse(geojson, {
                id: new Date().getTime().toString(),
                title: defaultDescription.title,
                subtitle: defaultDescription.subtitle
            });
        }
    }
    cleanStorage() {
        localStorageAPI.remove();
    }
    _parseToGeojson(rawData, fileExtension = ''){
        if(fileExtension && !supportedRouteFileExtension.includes(fileExtension)) {
            this.publish('errorEmit');
            return;
        }
        let geoJsonDraft = '';

        if(fileExtension === gpxExtension) {
            geoJsonDraft = this._parseGpxToGeojson(rawData);
        } else if(typeof rawData === 'string' && rawData !== '') {
            geoJsonDraft = this._safeParseStringToJson(rawData);
        }

        if(!this._isGeoJSONValid(geoJsonDraft)) {
            this.publish('errorEmit');
            return;
        }
        this.publish('validGeoJSONFile');

        return geoJsonDraft;
    }
    _parseGpxToGeojson(rawData) {
        const domXML = (new DOMParser()).parseFromString(rawData, 'text/xml');
        return  togeojson.gpx(domXML);
    }
    _safeParseStringToJson(data) {
        try {
            return JSON.parse(data);
        } catch (error) {
            this.publish('errorEmit');
            //next line only for collecting data, what customer try to use as a source
            sentryInstance.sendInfoReport(`catch error on safe parse string to json ${error.message} ${data}`);
            return;
        }
    }
    _requestGeoJsonData (fileName) {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `/routes/${fileName}`,
                type: "GET",
                success: function(data){
                    resolve(data)
                },
                error: function(errors){
                    reject(errors);
                }
            });
        });
    }
    _parseSuccessResponse(geoJson, routeData) {
        const cleanGeoJsonObj = this._keepOnlyLineFeature(geoJson);
        if(!cleanGeoJsonObj || !cleanGeoJsonObj.features.length) {
            this.publish('errorEmit');
            return;
        }
        const coordinates = this._getCoordinates(cleanGeoJsonObj);
        const bounds = this._getLngLatBoundsRectangle(coordinates);
        this._routeSettings.prevRouteId = this._routeSettings.routeId;
        this._routeSettings.routeId = routeData.id;
        this._routeSettings.title = routeData.title;
        this._routeSettings.subtitle = routeData.subtitle;
        this._routeSettings.tagline = routeData.tagline;
        this._routeSettings.geoJson = cleanGeoJsonObj;
        this._routeSettings.bounds = bounds;
        this._routeSettings.enableFitToTheBounds = true;
        localStorageAPI.update(this.getRouteSettings());
        this.publish('route:data:ready')
    }
    _keepOnlyLineFeature (geoJson){
        if(!geoJson.features || !Array.isArray(geoJson.features)) {
            return false;
        }

        const lineFeatures = geoJson.features.filter(feature => {
            const featureType = feature.geometry.type;
            return featureType === geoJsonFeaturesTypes.line || featureType === geoJsonFeaturesTypes.multiLine;
        });
        return Object.assign({}, geoJson, { features: lineFeatures })
    }
    _getCoordinates(geojsonObj) {
        let allCoordinates = [];
        geojsonObj.features.forEach(feature => {
            const featureType = feature.geometry.type;
            const coordinates = featureType === geoJsonFeaturesTypes.line
                ? feature.geometry.coordinates
                : this._extractMultiLineStringCoordinates(feature.geometry.coordinates);
            allCoordinates = allCoordinates.concat(coordinates)
        });
        return allCoordinates;
    }
    _extractMultiLineStringCoordinates(multiLineStringCoordinates){
        return multiLineStringCoordinates.reduce((allCoordinates, lineCoordinates) => allCoordinates.concat(lineCoordinates), []);
    }
    _getLngLatBoundsRectangle(lineCoordinates){

        const allLongitudeCoordinates = lineCoordinates.map(coordinate => coordinate[0]);
        const allLatitudeCoordinates = lineCoordinates.map(coordinate => coordinate[1]);
        let northPoint = null;
        let southPoint = null;

        allLatitudeCoordinates.forEach(coordinate => {
            if(northPoint === null || coordinate > northPoint) {
                northPoint = coordinate
            }
            if(southPoint === null ||coordinate < southPoint) {
                southPoint = coordinate
            }
        });

        let eastPoint = null;
        let westPoint = null;

        allLongitudeCoordinates.forEach(coordinate => {
            if(eastPoint === null || coordinate > eastPoint) {
                eastPoint = coordinate
            }
            if(westPoint === null || coordinate < westPoint) {
                westPoint = coordinate
            }
        });
        return [[westPoint, southPoint], [eastPoint, northPoint]];
    }
    _readRouteFile(file) {
        const fileExtension = file.name.split('.').pop();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = function (event) {
                resolve({
                    result: event.target.result,
                    fileExtension
                });
            };
            reader.readAsText(file);
        });
    }
    _isGeoJSONValid (geojson) {
        return geojson && !geojsonhint.hint(geojson).length;
    }
    _logCustomRouteData(geojson) {
        if(typeof _LTracker === "undefined") { return; }
        _LTracker.push({
            customRoute: geojson
        });
    }
}
